import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, UserPlus, UserCheck, Heart, MessageCircle, Eye, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => {
  if (!n) return "0";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
};

const PlyxProfile = () => {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user: me } = usePlyxAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);

  useEffect(() => {
    setLoading(true);
    plyxApi.get(`/plyx/u/${handle}`)
      .then((r) => {
        setData(r.data);
        setFollowing(r.data.i_follow);
      })
      .catch(() => toast.error("Profile not found"))
      .finally(() => setLoading(false));
  }, [handle]);

  const toggleFollow = async () => {
    if (!me) { navigate("/plyx/login"); return; }
    const next = !following;
    setFollowing(next);
    try {
      if (next) await plyxApi.post(`/plyx/u/${handle}/follow`);
      else await plyxApi.delete(`/plyx/u/${handle}/follow`);
    } catch {
      setFollowing(!next);
      toast.error("Could not update follow");
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  }
  if (!data) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">Profile not found</div>;
  }

  const u = data.user;
  const isMe = me && me.plyx_user_id === u.plyx_user_id;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid={`plyx-profile-${u.handle}`}>
      <div className="container-narrow py-10">
        <div className="flex items-start gap-5 sm:gap-8 flex-wrap">
          <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-white/10 overflow-hidden flex items-center justify-center text-3xl font-display">
            {u.avatar_url
              ? <img src={plyxMediaUrl(u.avatar_url)} alt={u.handle} className="w-full h-full object-cover"/>
              : u.display_name?.slice(0, 1) || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight">@{u.handle}</h1>
            <div className="text-white/70 mt-0.5">{u.display_name}</div>
            <div className="flex gap-5 mt-3 text-sm">
              <div data-testid="plyx-profile-follower-count"><strong>{fmt(u.follower_count)}</strong> <span className="text-white/50">followers</span></div>
              <div data-testid="plyx-profile-following-count"><strong>{fmt(u.following_count)}</strong> <span className="text-white/50">following</span></div>
              <div data-testid="plyx-profile-video-count"><strong>{fmt(u.video_count)}</strong> <span className="text-white/50">videos</span></div>
            </div>
            {u.bio && <p className="mt-3 text-white/80 max-w-md whitespace-pre-wrap">{u.bio}</p>}
            {u.country && <div className="mt-1 text-xs text-white/50">{u.country}</div>}
            {!isMe ? (
              <button
                type="button"
                onClick={toggleFollow}
                data-testid="plyx-profile-follow-btn"
                className={`mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                  following ? "bg-white/15 text-white hover:bg-white/25" : "text-[#0A0A0A]"
                }`}
                style={following ? {} : { background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
              >
                {following ? <><UserCheck className="w-4 h-4"/> Following</> : <><UserPlus className="w-4 h-4"/> Follow</>}
              </button>
            ) : (
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to="/plyx/upload"
                  data-testid="plyx-profile-upload-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-[#0A0A0A]"
                  style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
                >
                  Upload a video
                </Link>
                <Link
                  to="/plyx/settings"
                  data-testid="plyx-profile-edit-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold border border-white/20 hover:bg-white/10"
                >
                  Edit profile
                </Link>
                <Link
                  to="/plyx/wallet"
                  data-testid="plyx-profile-wallet-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold border border-white/20 hover:bg-white/10"
                >
                  Wallet
                </Link>
                <Link
                  to="/plyx/shop/manage"
                  data-testid="plyx-profile-shop-manage-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold border border-[#FFB42D]/40 text-[#FFB42D] hover:bg-[#FFB42D]/10"
                >
                  Manage Shop
                </Link>
              </div>
            )}
            {/* Always show "View Shop" for any profile if products > 0 (or just always — shop page handles empty) */}
            {!isMe && (
              <Link
                to={`/plyx/u/${handle}/shop`}
                data-testid="plyx-profile-shop-view-btn"
                className="mt-2 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold border border-[#FFB42D]/40 text-[#FFB42D] hover:bg-[#FFB42D]/10"
              >
                <ShoppingBag className="w-3.5 h-3.5"/> Visit @{handle}'s shop
              </Link>
            )}
          </div>
        </div>

        {/* Video grid */}
        <div className="mt-10 grid grid-cols-3 sm:grid-cols-4 gap-1 sm:gap-2">
          {data.videos.length === 0 && (
            <div className="col-span-full text-center text-white/50 py-16">No videos yet.</div>
          )}
          {data.videos.map((v) => (
            <Link
              key={v.video_id}
              to={`/plyx/v/${v.video_id}`}
              data-testid={`plyx-profile-video-${v.video_id}`}
              className="relative aspect-[9/16] bg-black rounded-md overflow-hidden group"
            >
              {v.thumbnail_url ? (
                <img src={plyxMediaUrl(v.thumbnail_url)} alt="" className="w-full h-full object-cover"/>
              ) : (
                <video src={plyxMediaUrl(v.video_url)} className="w-full h-full object-cover" muted playsInline preload="metadata"/>
              )}
              <div className="absolute bottom-1.5 left-2 right-2 flex items-center justify-between text-xs text-white">
                <span className="inline-flex items-center gap-1 bg-black/50 backdrop-blur px-1.5 py-0.5 rounded">
                  <Eye className="w-3 h-3"/> {fmt(v.view_count)}
                </span>
                <span className="inline-flex items-center gap-1 bg-black/50 backdrop-blur px-1.5 py-0.5 rounded">
                  <Heart className="w-3 h-3"/> {fmt(v.like_count)}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlyxProfile;
